import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AGENT_PLAN, APP_HOME_URL, deskUrl, SEAT_TIERS } from "../config";
import { formatPlanPrice, resolvePaidPlan } from "@billing/plans.js";
import LegalOnboardingFrame from "../components/LegalOnboardingFrame";

const BAKED_CLIENT_ID = String(import.meta.env.VITE_PAYPAL_CLIENT_ID || "").trim();
const PAUSE_COPY = "Checkout isn't available just now. Please try again shortly.";
const LEGAL_ACCEPT_KEY = "dh_j_legal_onboarding_accepted";

function publicPaypalMessage(message) {
  const raw = String(message || "").trim();
  if (!raw || /env\.local|PAYPAL_|secret|webhook|CLIENT_ID|not configured/i.test(raw)) {
    return PAUSE_COPY;
  }
  return raw;
}

function loadPaypalScript(clientId) {
  const existing = document.querySelector("script[data-paypal-sdk]");
  if (existing && existing.dataset.paypalClientId === clientId && window.paypal) {
    return Promise.resolve(window.paypal);
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=USD&intent=capture`;
    script.dataset.paypalSdk = "true";
    script.dataset.paypalClientId = clientId;
    script.onload = () => resolve(window.paypal);
    script.onerror = () => reject(new Error("PayPal could not load."));
    if (existing) existing.remove();
    document.body.appendChild(script);
  });
}

export default function Pay() {
  const [params] = useSearchParams();
  const planParam = String(params.get("plan") || "agent").toLowerCase();
  const plan =
    planParam === "broker"
      ? "broker"
      : planParam === "corporate" || planParam === "enterprise"
        ? "corporate"
        : "agent";
  const tierId = params.get("tier") || (plan === "broker" ? "10" : "");
  const cycle = String(params.get("cycle") || "monthly").toLowerCase() === "annual" ? "annual" : "monthly";
  const user = params.get("user") || "";
  const role = params.get("role") || plan;
  const paid = resolvePaidPlan({ plan, tier: tierId, cycle });
  const seat = SEAT_TIERS.find((item) => item.id === tierId) || SEAT_TIERS[0];
  const label =
    plan === "broker"
      ? `Broker Office · ${seat.name}`
      : paid.label;
  const [config, setConfig] = useState(
    BAKED_CLIENT_ID ? { connected: false, clientId: BAKED_CLIENT_ID } : null
  );
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [pendingRedirect, setPendingRedirect] = useState(null);
  const buttonsRef = useRef(null);
  const clientId = String(config?.clientId || BAKED_CLIENT_ID || "").trim();
  const useServer = Boolean(config?.connected && clientId && !config?.mock);
  const mockMode = Boolean(config?.mock);

  async function runMockCheckout() {
    setError("");
    setStatus("Recording sandbox payment…");
    try {
      const orderRes = await fetch("/api/paypal/orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan, tier: tierId, user, role, cycle }),
      });
      const orderBody = await orderRes.json().catch(() => ({}));
      if (!orderRes.ok || !orderBody.id) throw new Error(publicPaypalMessage(orderBody.error));
      const capRes = await fetch(`/api/paypal/orders/${orderBody.id}/capture`, { method: "POST" });
      const body = await capRes.json().catch(() => ({}));
      if (!capRes.ok) throw new Error(publicPaypalMessage(body.error));
      const next = new URL(returnUrl);
      next.searchParams.set("order", body.orderId || orderBody.id);
      if (body.amount) next.searchParams.set("amount", String(body.amount));
      setStatus("Sandbox payment recorded. Accept the agreements to return to REChecklist.");
      setPendingRedirect(next.toString());
    } catch (err) {
      setError(publicPaypalMessage(err.message || "Sandbox checkout failed."));
      setStatus("");
    }
  }

  const returnUrl = useMemo(() => {
    const path = deskUrl(role);
    const url = new URL(path, APP_HOME_URL);
    url.searchParams.set("checkout", "success");
    if (user) url.searchParams.set("user", user);
    if (role) url.searchParams.set("role", role);
    url.searchParams.set("plan", paid.id);
    return url;
  }, [paid.id, role, user]);

  function continueAfterLegal(nextUrl) {
    try {
      sessionStorage.setItem(
        LEGAL_ACCEPT_KEY,
        JSON.stringify({ at: new Date().toISOString(), plan: paid.id, cycle })
      );
    } catch {
      /* ignore */
    }
    window.location.assign(String(nextUrl));
  }

  useEffect(() => {
    let cancelled = false;
    fetch("/api/paypal/config")
      .then(async (response) => {
        const type = response.headers.get("content-type") || "";
        if (!response.ok || !type.includes("json")) throw new Error("offline");
        return response.json();
      })
      .then((body) => {
        if (cancelled) return;
        const nextId = String(body.clientId || BAKED_CLIENT_ID || "").trim();
        setConfig({
          connected: Boolean(body.connected && nextId),
          clientId: nextId,
          mock: Boolean(body.mock),
        });
      })
      .catch(() => {
        if (!cancelled) setConfig({ connected: false, clientId: BAKED_CLIENT_ID });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (mockMode || !clientId || !buttonsRef.current || pendingRedirect) return undefined;
    let buttons;
    loadPaypalScript(clientId)
      .then((paypal) => {
        if (!paypal?.Buttons || !buttonsRef.current) return;
        buttons = paypal.Buttons({
          style: { layout: "vertical", color: "gold", shape: "rect", label: "paypal" },
          createOrder: async (_data, actions) => {
            setError("");
            if (useServer) {
              const response = await fetch("/api/paypal/orders", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ plan, tier: tierId, user, role, cycle }),
              });
              const body = await response.json().catch(() => ({}));
              if (!response.ok || !body.id) throw new Error(publicPaypalMessage(body.error));
              return body.id;
            }
            return actions.order.create({
              purchase_units: [
                {
                  custom_id: [user || "guest", paid.id, role || paid.sku].join("|").slice(0, 127),
                  description: paid.label,
                  amount: { currency_code: paid.currency, value: paid.amount },
                },
              ],
            });
          },
          onApprove: async (data, actions) => {
            if (useServer) {
              const response = await fetch(`/api/paypal/orders/${data.orderID}/capture`, {
                method: "POST",
              });
              const body = await response.json().catch(() => ({}));
              if (!response.ok) throw new Error(publicPaypalMessage(body.error));
              const next = new URL(returnUrl);
              next.searchParams.set("order", body.orderId || data.orderID);
              if (body.amount) next.searchParams.set("amount", String(body.amount));
              if (body.payerEmail) next.searchParams.set("email", body.payerEmail);
              setStatus("Payment received. Please review and accept the agreements to continue.");
              setPendingRedirect(next.toString());
              return;
            }
            const details = await actions.order.capture();
            const unit = details?.purchase_units?.[0] || {};
            const capture = unit.payments?.captures?.[0] || {};
            const next = new URL(returnUrl);
            next.searchParams.set("order", details.id || data.orderID);
            if (capture.amount?.value) next.searchParams.set("amount", String(capture.amount.value));
            if (details.payer?.email_address) {
              next.searchParams.set("email", details.payer.email_address);
            }
            setStatus("Payment received. Please review and accept the agreements to continue.");
            setPendingRedirect(next.toString());
          },
          onCancel: () =>
            setStatus("Checkout was left unfinished. You can try again when you are ready."),
          onError: (err) =>
            setError(publicPaypalMessage(err.message || "PayPal could not complete that step.")),
        });
        return buttons.render(buttonsRef.current);
      })
      .catch((err) =>
        setError(publicPaypalMessage(err.message || "PayPal is unavailable right now."))
      );
    return () => {
      try {
        buttons?.close?.();
      } catch {
        /* ignore */
      }
    };
  }, [clientId, cycle, mockMode, paid, pendingRedirect, plan, returnUrl, role, tierId, useServer, user]);

  if (pendingRedirect) {
    return (
      <article>
        <h1>Almost there</h1>
        <p className="lead">
          Payment details are complete. Accept the unified legal and policy frame to finish and open
          REChecklist.
        </p>
        <LegalOnboardingFrame onAccept={() => continueAfterLegal(pendingRedirect)} />
      </article>
    );
  }

  return (
    <article>
      <h1>Subscribe</h1>
      <p className="lead">
        PayPal holds the payment. After a successful checkout you return to REChecklist. This site
        stays a workflow, not a storefront.
      </p>
      <div className="card paypal-frame" style={{ maxWidth: "28rem" }}>
        <p className="paypal-mark">PayPal</p>
        <p>
          <strong>{label}</strong>
        </p>
        <p className="muted">
          {formatPlanPrice(paid)} · billed {paid.cycle === "annual" ? "annually" : "monthly"}
        </p>
        <p className="muted">
          By paying you agree to the{" "}
          <Link to="/purchase-agreement">Purchase Agreement</Link> (no refunds; cancel anytime with
          72-hour notice before renewal).
        </p>
        {config == null ? <p className="muted">Connecting checkout…</p> : null}
        {config && !clientId && !mockMode ? (
          <p className="muted">
            {PAUSE_COPY}{" "}
            <Link to="/contact">Write to us</Link>
          </p>
        ) : null}
        {mockMode ? (
          <>
            <p className="muted">
              PayPal credentials are not loaded — use sandbox simulate (no card charged).
            </p>
            <button type="button" className="primary-btn" onClick={runMockCheckout}>
              Simulate sandbox payment
            </button>
          </>
        ) : (
          <div ref={buttonsRef} />
        )}
        {status ? <p>{status}</p> : null}
        {error ? (
          <p className="muted">
            {error} <Link to="/contact">Write to us</Link>
          </p>
        ) : null}
        <p className="muted" style={{ marginTop: "1rem" }}>
          Agent Standard starts at ${AGENT_PLAN.monthly}/month (or ${AGENT_PLAN.annual}/year). Broker
          Office seats stay on their own plans.
        </p>
      </div>
    </article>
  );
}
